import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import {
  PRESCRIPTION_STATUS_LABELS,
  PRACTITIONER_ROLE_LABELS,
  formatMonthKey,
  formatPractitionerLabel
} from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import StatisticalDataTable, { statPercentColumn, statRankColumn } from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE } from './charts/chartTheme';
import {
  computeShares,
  lineChartData,
  statusPieData,
  doughnutChartData,
  truncateLabel
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export default function HeadDoctorReportsOrdonnancesPage() {
  const { data, summary, loading } = useClinicalReports();
  const byStatus = data?.prescriptions?.by_status || [];
  const byMonth = data?.prescriptions?.by_month || [];
  const topDoctors = useMemo(
    () => computeShares(data?.prescriptions?.top_doctors || [], 'prescription_count'),
    [data]
  );

  const totalRx = Number(summary.prescriptions || 0);
  const activeRx = Number(summary.prescription_active || 0);
  const fulfilledRx = Number(summary.prescription_fulfilled || 0);
  const cancelledRx = Number(summary.prescription_cancelled || 0);

  const statusPie = useMemo(
    () => statusPieData(byStatus, PRESCRIPTION_STATUS_LABELS),
    [byStatus]
  );

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_PRESCRIPTIONS,
            data: byMonth.map((row) => Number(row.count)),
            color: CHART_PALETTE[1],
            fill: true
          }
        ]
      ),
    [byMonth]
  );

  const doctorsBarData = useMemo(
    () => ({
      labels: topDoctors.map((r) => truncateLabel(formatPractitionerLabel(r), 28)),
      datasets: [
        {
          label: COPY.CHART_PRESCRIPTION_COUNT,
          data: topDoctors.map((r) => Number(r.prescription_count)),
          backgroundColor: CHART_PALETTE[0],
          borderRadius: 6,
          maxBarThickness: 40
        }
      ]
    }),
    [topDoctors]
  );

  const tableColumns = [
    {
      key: 'rank',
      label: '#',
      className: 'text-muted',
      render: (_row, idx) => idx + 1
    },
    {
      key: 'doctor_name',
      label: COPY.COL_DOCTOR,
      className: 'fw-semibold',
      render: (row) => row.doctor_name
    },
    {
      key: 'practitioner_role',
      label: COPY.COL_PRACTITIONER_ROLE,
      className: 'text-muted',
      render: (row) =>
        PRACTITIONER_ROLE_LABELS[row.practitioner_role] || row.practitioner_role || '—'
    },
    statRankColumn(COPY.COL_PRESCRIPTIONS, 'prescription_count'),
    statPercentColumn(COPY.COL_SHARE)
  ];

  const empty = !loading && totalRx === 0;
  const hasCharts = !loading && !empty && (byStatus.length > 0 || byMonth.length > 0);

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_ORDONNANCES}
        title={COPY.PAGE_ORDONNANCES_TITLE}
        description={COPY.PAGE_ORDONNANCES_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <HeadDoctorStatCard
            title={COPY.KPI_RX_TOTAL}
            value={totalRx}
            icon="pills"
            tone="indigo"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <HeadDoctorStatCard
            title={COPY.KPI_RX_ACTIVE}
            value={activeRx}
            icon="alertCircle"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <HeadDoctorStatCard
            title={COPY.KPI_RX_FULFILLED}
            value={fulfilledRx}
            icon="check"
            tone="teal"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <HeadDoctorStatCard
            title={COPY.KPI_RX_CANCELLED}
            value={cancelledRx}
            icon="times"
            tone="slate"
            loading={loading}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : hasCharts ? (
        <>
          <ReportsChartGrid>
            <ReportsChartCol lg={4}>
              <ReportChartCard
                title={COPY.CHART_RX_STATUS_TITLE}
                subtitle={COPY.CHART_RX_STATUS_DESC}
                height={300}
              >
                <ClinicalDoughnutChart
                  data={doughnutChartData(statusPie.labels, statusPie.values, statusPie.colors)}
                />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={8}>
              <ReportChartCard
                title={COPY.CHART_RX_TREND_TITLE}
                subtitle={COPY.CHART_RX_TREND_DESC}
                height={300}
              >
                <ClinicalLineChart
                  data={trendData}
                  xLabel={COPY.CHART_AXIS_PERIOD}
                  yLabel={COPY.CHART_AXIS_ORDONNANCES}
                />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>

          {topDoctors.length > 0 ? (
            <ReportsChartGrid>
              <ReportsChartCol lg={12}>
                <ReportChartCard
                  title={COPY.CHART_DOCTORS_BAR_TITLE}
                  subtitle={COPY.CHART_DOCTORS_BAR_DESC}
                  height={280}
                >
                  <ClinicalBarChart
                    data={doctorsBarData}
                    yLabel={COPY.CHART_AXIS_ORDONNANCES}
                    xLabel={COPY.CHART_AXIS_PRESCRIBERS}
                  />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          ) : null}
        </>
      ) : (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_PRESCRIPTIONS}</p>
        </div>
      )}

      {topDoctors.length > 0 ? (
        <>
          <h3 className="hd-reports-table-title">{COPY.TABLE_DOCTORS_TITLE}</h3>
          <StatisticalDataTable
            columns={tableColumns}
            rows={topDoctors}
            rowKey="doctor_id"
            maxValueKey="prescription_count"
            loading={loading}
            emptyLabel={COPY.EMPTY_DOCTORS}
            loadingLabel={COPY.LOADING}
          />
        </>
      ) : null}
    </>
  );
}
